import React, { useCallback, useMemo } from 'react';
import { useField } from 'react-final-form';

import LoadingIndicator from '@dashboard/components/loading/LoadingIndicator';
import { useSetCurrentBlockId } from '@wp-block-builder/editor/hooks';
import EditableMessage from '@wp-block-builder/views/EditableMessage';

import { renderLiquid, WpRendererError } from '@waypoint/templates';

import MessageArea from '../components/MessageArea';
import { useCurrentLayoutConfiguration, useCurrentPreviewVariablesObject } from '../hooks';

import ErrorMessages from './ErrorMessages';

function handleRender(template: string, callback: () => string) {
  try {
    return { value: callback(), error: null };
  } catch (e) {
    if (e instanceof WpRendererError) {
      return { value: template, error: e };
    }
    throw e;
  }
}

function useRenderedPreheader() {
  const template = useField<string>('configuration.preheaderTemplate').input.value;
  const strictVariablesRendering = useField<boolean>('configuration.strictVariablesRendering').input.value;
  const variables = useCurrentPreviewVariablesObject();

  return useMemo(
    () =>
      handleRender(template, () =>
        renderLiquid({
          strictVariablesRendering,
          skipLiquid: false,
          template,
          variables,
        })
      ),
    [strictVariablesRendering, template, variables]
  );
}

function useRenderedSubject() {
  const template = useField<string>('configuration.subjectTemplate').input.value;
  const strictVariablesRendering = useField<boolean>('configuration.strictVariablesRendering').input.value;
  const variables = useCurrentPreviewVariablesObject();

  return useMemo(
    () =>
      handleRender(template, () =>
        renderLiquid({
          strictVariablesRendering,
          skipLiquid: false,
          template,
          variables,
        })
      ),
    [strictVariablesRendering, template, variables]
  );
}

export default function EditBody() {
  const { loading, data: layoutConfiguration } = useCurrentLayoutConfiguration();
  const configuration = useField('configuration').input.value;
  const setCurrentBlockId = useSetCurrentBlockId();

  const variables = useCurrentPreviewVariablesObject();
  const subject = useRenderedSubject();
  const preheader = useRenderedPreheader();

  const onBackdropClick = useCallback(() => {
    setCurrentBlockId(null);
  }, [setCurrentBlockId]);

  const renderChildren = () => {
    if (loading) {
      return <LoadingIndicator />;
    }
    return <EditableMessage {...{ configuration, layoutConfiguration, variables }} />;
  };

  return (
    <MessageArea subject={subject.value} preheader={preheader.value} onClick={onBackdropClick}>
      <ErrorMessages subjectError={subject.error} preheaderError={preheader.error} />
      {renderChildren()}
    </MessageArea>
  );
}
